/**
 * Movento — calculadora de frete na página do produto.
 *
 * Usa as taxas de envio nativas da Shopify (/cart/shipping_rates.json), então
 * funciona com qualquer app de frete que se registre como transportadora
 * (Melhor Envio, Frenet, Kangu, Correios etc.) sem precisar de código extra.
 *
 * Como a API só calcula frete para o carrinho, o produto é adicionado ao
 * carrinho temporariamente e o carrinho é restaurado ao estado original logo
 * em seguida — inclusive se der erro no meio do caminho.
 */
import { StandardEvents } from '@shopify/events';

const RAIZ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const MOEDA = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL';
const CHAVE_CEP = 'movento:cep';

const formatador = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: MOEDA });

/** @param {string} valor */
function formatarMoeda(valor) {
  const numero = Number.parseFloat(valor);
  if (!Number.isFinite(numero)) return '';
  return formatador.format(numero);
}

/** @param {string} digitos */
function formatarCep(digitos) {
  const limpo = digitos.replace(/\D/g, '').slice(0, 8);
  if (limpo.length <= 5) return limpo;
  return `${limpo.slice(0, 5)}-${limpo.slice(5)}`;
}

/** @param {string} texto */
function escapar(texto) {
  return String(texto ?? '').replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

async function lerJson(resposta) {
  try {
    return await resposta.json();
  } catch (_) {
    return null;
  }
}

/** Prazo de entrega a partir dos campos que a Shopify devolve. */
function descreverPrazo(taxa) {
  const dias = Array.isArray(taxa.delivery_days) ? taxa.delivery_days.filter((d) => Number.isFinite(d)) : [];
  if (dias.length) {
    const maximo = Math.max(...dias);
    return maximo === 1 ? 'em até 1 dia útil' : `em até ${maximo} dias úteis`;
  }

  const intervalo = Array.isArray(taxa.delivery_range) ? taxa.delivery_range.filter(Boolean) : [];
  if (intervalo.length) {
    const fim = new Date(intervalo[intervalo.length - 1]);
    if (!Number.isNaN(fim.getTime())) {
      return `até ${fim.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
    }
  }

  return '';
}

class MoventoShipping extends HTMLElement {
  #section = null;
  #ocupado = false;

  connectedCallback() {
    this.form = this.querySelector('[data-frete-form]');
    this.input = this.querySelector('[data-frete-cep]');
    this.botao = this.querySelector('[data-frete-submit]');
    this.resultados = this.querySelector('[data-frete-results]');
    this.mensagem = this.querySelector('[data-frete-message]');

    if (!this.form || !this.input) return;

    this.form.addEventListener('submit', this.#aoEnviar);
    this.input.addEventListener('input', this.#aoDigitar);

    try {
      const salvo = localStorage.getItem(CHAVE_CEP);
      if (salvo) this.input.value = formatarCep(salvo);
    } catch (_) {
      /* localStorage indisponível */
    }

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#aoTrocarVariante);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#aoEnviar);
    this.input?.removeEventListener('input', this.#aoDigitar);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#aoTrocarVariante);
    this.#section = null;
  }

  #aoTrocarVariante = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const variante = detail?.resource;
        if (!variante?.id) return;
        if (String(variante.id) === this.dataset.variantId) return;
        this.dataset.variantId = String(variante.id);
        this.#limpar();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[movento-frete] evento rejeitado:', error);
      });
  };

  #aoDigitar = () => {
    const posicaoFinal = this.input.selectionStart === this.input.value.length;
    this.input.value = formatarCep(this.input.value);
    if (posicaoFinal) this.input.setSelectionRange(this.input.value.length, this.input.value.length);
  };

  #aoEnviar = (event) => {
    event.preventDefault();
    if (this.#ocupado) return;
    this.#calcular(this.input.value.replace(/\D/g, ''));
  };

  #limpar() {
    if (this.resultados) {
      this.resultados.innerHTML = '';
      this.resultados.hidden = true;
    }
    this.#dizer('');
  }

  /** @param {string} texto @param {'erro'|'info'|''} [tipo] */
  #dizer(texto, tipo = 'info') {
    if (!this.mensagem) return;
    this.mensagem.textContent = texto;
    this.mensagem.hidden = !texto;
    this.mensagem.dataset.tipo = texto ? tipo : '';
  }

  #carregando(estado) {
    this.#ocupado = estado;
    if (this.botao) {
      this.botao.disabled = estado;
      this.botao.setAttribute('aria-busy', String(estado));
    }
  }

  get #quantidade() {
    const campo = this.#section?.querySelector('input[name="quantity"]');
    const valor = campo ? Number.parseInt(campo.value, 10) : 1;
    return Number.isFinite(valor) && valor > 0 ? valor : 1;
  }

  /** @param {string} digitos */
  async #calcular(digitos) {
    if (digitos.length !== 8) {
      this.#limpar();
      this.#dizer('Digite um CEP válido, com 8 números.', 'erro');
      return;
    }

    const variantId = this.dataset.variantId;
    if (!variantId) {
      this.#dizer('Selecione uma variação do produto para calcular o frete.', 'erro');
      return;
    }

    this.#limpar();
    this.#carregando(true);
    this.#dizer('Calculando frete...');

    try {
      localStorage.setItem(CHAVE_CEP, digitos);
    } catch (_) {
      /* segue sem salvar */
    }

    const endereco = await this.#buscarEndereco(digitos);
    if (endereco === 'invalido') {
      this.#carregando(false);
      this.#dizer('CEP não encontrado. Confira o número digitado.', 'erro');
      return;
    }

    let quantidadeOriginal = 0;
    let adicionado = false;

    try {
      quantidadeOriginal = await this.#quantidadeNoCarrinho(variantId);

      const adicionar = await fetch(`${RAIZ}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: this.#quantidade }] }),
      });

      if (!adicionar.ok) {
        const erro = await lerJson(adicionar);
        throw new Error(erro?.description || 'Não foi possível calcular o frete para este produto.');
      }
      adicionado = true;

      const taxas = await this.#buscarTaxas(digitos, endereco);
      this.#renderizar(taxas, digitos, endereco);
    } catch (erro) {
      console.warn('[movento-frete]', erro);
      this.#dizer(erro?.message || 'Não conseguimos calcular o frete agora. Tente novamente.', 'erro');
    } finally {
      if (adicionado) await this.#restaurarCarrinho(variantId, quantidadeOriginal);
      this.#carregando(false);
    }
  }

  /** @param {string} digitos */
  async #buscarEndereco(digitos) {
    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      if (!resposta.ok) return null;
      const dados = await resposta.json();
      if (dados?.erro) return 'invalido';
      return { cidade: dados.localidade, uf: dados.uf };
    } catch (_) {
      return null; // segue sem cidade/UF; a Shopify ainda calcula pelo CEP
    }
  }

  /** @param {string} variantId */
  async #quantidadeNoCarrinho(variantId) {
    const resposta = await fetch(`${RAIZ}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const carrinho = await lerJson(resposta);
    const linha = carrinho?.items?.find((item) => String(item.variant_id) === String(variantId));
    return linha ? linha.quantity : 0;
  }

  /** @param {string} variantId @param {number} quantidade */
  async #restaurarCarrinho(variantId, quantidade) {
    try {
      const resposta = await fetch(`${RAIZ}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const carrinho = await lerJson(resposta);
      const linha = carrinho?.items?.find((item) => String(item.variant_id) === String(variantId));
      if (!linha) return;

      await fetch(`${RAIZ}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: linha.key, quantity: quantidade }),
      });
    } catch (erro) {
      console.warn('[movento-frete] falha ao restaurar o carrinho:', erro);
    }
  }

  /** @param {string} digitos */
  async #buscarTaxas(digitos, endereco) {
    const paises = [this.dataset.country || 'Brazil', 'BR'];

    for (const pais of paises) {
      const parametros = new URLSearchParams();
      parametros.set('shipping_address[zip]', formatarCep(digitos));
      parametros.set('shipping_address[country]', pais);
      if (endereco?.uf) parametros.set('shipping_address[province]', endereco.uf);

      const resposta = await fetch(`${RAIZ}cart/shipping_rates.json?${parametros}`, {
        headers: { Accept: 'application/json' },
      });
      const dados = await lerJson(resposta);

      if (resposta.ok && Array.isArray(dados?.shipping_rates)) return dados.shipping_rates;
    }

    return [];
  }

  #renderizar(taxas, digitos, endereco) {
    if (!this.resultados) return;

    if (!taxas.length) {
      this.#dizer('Não encontramos opções de entrega para este CEP.', 'erro');
      return;
    }

    const local = endereco?.cidade ? `${endereco.cidade} — ${endereco.uf}` : formatarCep(digitos);
    this.#dizer(`Opções de entrega para ${local}`);

    const linhas = taxas
      .map((taxa) => {
        const nome = escapar(taxa.presentment_name || taxa.name || 'Entrega');
        const prazo = escapar(descreverPrazo(taxa));
        const valor = Number.parseFloat(taxa.price) === 0 ? 'Grátis' : formatarMoeda(taxa.price);
        return `
          <li class="movento-frete__item">
            <span class="movento-frete__item-info">
              <span class="movento-frete__item-nome">${nome}</span>
              ${prazo ? `<span class="movento-frete__item-prazo">${prazo}</span>` : ''}
            </span>
            <span class="movento-frete__item-valor">${valor}</span>
          </li>`;
      })
      .join('');

    this.resultados.innerHTML = `<ul class="movento-frete__lista">${linhas}</ul>`;
    this.resultados.hidden = false;
  }
}

if (!customElements.get('movento-shipping')) {
  customElements.define('movento-shipping', MoventoShipping);
}
